import DefaultApiService from "./default";
import BasicInformationService from "./basicInformation";
import ContractorRegistrationApiService from "./contractorRegistration";

function unwrapApiData(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;

  const nested = response.data ?? response.Data;
  if (Array.isArray(nested)) return nested;

  if (nested && typeof nested === "object") {
    const inner = nested.data ?? nested.Data;
    if (Array.isArray(inner)) return inner;
  }

  return [];
}

function isApiSuccess(response) {
  if (!response) return false;
  if (response.isSuccess === true || response.IsSuccess === true) return true;
  return unwrapApiData(response).length > 0;
}

function toApiResult(data) {
  return { isSuccess: true, data: unwrapApiData(data) };
}

async function tryLoad(loader) {
  try {
    const response = await loader();
    if (!isApiSuccess(response)) return null;
    const rows = unwrapApiData(response);
    return rows.length ? toApiResult(rows) : null;
  } catch (e) {
    console.warn("Location list load failed:", e);
    return null;
  }
}

/**
 * استان/شهر برای فرم درخواست مشتری.
 * ترتیب: Customer/Requests → Profile → ContractorRegistration (بدون BI/Grid برای شهر)
 */
async function getProvincesForCustomerRequest() {
  const customer = await tryLoad(() =>
    DefaultApiService.getApi("SLN/Customer/Requests/GetProvinces")
  );
  if (customer) return customer;

  const bi = await tryLoad(() =>
    BasicInformationService.getProvinceInformationListApi()
  );
  if (bi) return bi;

  const profile = await tryLoad(() =>
    DefaultApiService.getApi("Profile/GetProvinces")
  );
  if (profile) return profile;

  const reg = await tryLoad(() => ContractorRegistrationApiService.getProvinces());
  if (reg) return reg;

  const citiesRes = await getCitiesForCustomerRequest();
  const cities = mapCitiesToDropdown(citiesRes);
  const derived = deriveProvincesFromCities(cities);
  if (derived.length) {
    return { isSuccess: true, data: derived };
  }

  return { isSuccess: false, data: [] };
}

function deriveProvincesFromCities(cities) {
  const map = new Map();
  for (const c of cities) {
    const pid = c.provinceId;
    if (pid == null || map.has(pid)) continue;
    const name = c.provinceName || c.ProvinceName;
    if (!name) continue;
    map.set(pid, { id: pid, provinceName: name });
  }
  return Array.from(map.values());
}

async function getCitiesForCustomerRequest() {
  const customer = await tryLoad(() =>
    DefaultApiService.getApi("SLN/Customer/Requests/GetCities")
  );
  if (customer) return customer;

  const profile = await tryLoad(() => DefaultApiService.getApi("Profile/GetCities"));
  if (profile) return profile;

  const reg = await tryLoad(() => ContractorRegistrationApiService.getCities());
  if (reg) return reg;

  return { isSuccess: false, data: [] };
}

/**
 * Loads canonical province/city lists for customer request submit.
 * Always prefers Customer API (same source as backend validation).
 */
async function loadCustomerRequestLocationLists() {
  const [provincesResponse, citiesResponse] = await Promise.all([
    getProvincesForCustomerRequest(),
    getCitiesForCustomerRequest(),
  ]);

  return {
    provinces: mapProvincesToDropdown(provincesResponse),
    cities: mapCitiesToDropdown(citiesResponse),
  };
}

function mapProvincesToDropdown(provincesResponse) {
  const rows = unwrapApiData(provincesResponse);
  if (!rows.length) return [];

  return rows.map((p) => {
    const id = p.id ?? p.Id;
    const name =
      p.provinceName ?? p.ProvinceName ?? p.title ?? p.Title ?? "";
    return {
      id,
      value: String(id),
      label: name,
      provinceName: name,
      provinceCode: p.provinceCode ?? p.ProvinceCode,
      externalId: p.externalId ?? p.ExternalId,
      latitude: p.latitude ?? p.Latitude,
      longitude: p.longitude ?? p.Longitude,
    };
  });
}

function mapCitiesToDropdown(citiesResponse) {
  const rawCityList = unwrapApiData(citiesResponse);

  return rawCityList.map((c) => {
    const id = c.id ?? c.Id;
    const cityName = c.cityName ?? c.CityName ?? "";
    const provinceIdVal = c.provinceId ?? c.ProvinceId;
    const provinceName = c.provinceName ?? c.ProvinceName;
    return {
      id,
      value: String(id),
      label: cityName,
      cityName,
      provinceName,
      cityCode: c.cityCode ?? c.CityCode,
      provinceId: provinceIdVal != null ? String(provinceIdVal) : "",
      externalId: c.externalId ?? c.ExternalId,
      latitude: c.latitude ?? c.Latitude,
      longitude: c.longitude ?? c.Longitude,
    };
  });
}

/**
 * Maps form province/city ids to canonical BI ids before API submit.
 * Prevents sending stale or mismatched ids when dropdown value and Formik drift.
 */
function resolveLocationIdsForSubmit(provinceId, cityId, provinceList, cityList) {
  const rawCityId = cityId != null && cityId !== "" ? String(cityId) : "";
  const rawProvinceId =
    provinceId != null && provinceId !== "" ? String(provinceId) : "";

  if (!rawCityId) {
    return { cityId: 0, provinceId: 0, valid: false };
  }

  const city = (cityList || []).find(
    (entry) =>
      String(entry.value) === rawCityId ||
      String(entry.id) === rawCityId ||
      Number(entry.value) === Number(rawCityId) ||
      Number(entry.id) === Number(rawCityId)
  );

  if (!city) {
    return {
      cityId: Number(rawCityId) || 0,
      provinceId: Number(rawProvinceId) || 0,
      valid: false,
    };
  }

  const resolvedCityId = Number(city.value ?? city.id);
  let resolvedProvinceId =
    city.provinceId != null && city.provinceId !== ""
      ? Number(city.provinceId)
      : 0;

  if ((!resolvedProvinceId || Number.isNaN(resolvedProvinceId)) && rawProvinceId) {
    const province = (provinceList || []).find(
      (entry) =>
        String(entry.value) === rawProvinceId ||
        String(entry.id) === rawProvinceId ||
        Number(entry.value) === Number(rawProvinceId)
    );
    resolvedProvinceId = province
      ? Number(province.value ?? province.id)
      : Number(rawProvinceId) || 0;
  }

  if (
    rawProvinceId &&
    resolvedProvinceId &&
    String(resolvedProvinceId) !== rawProvinceId
  ) {
    return {
      cityId: resolvedCityId,
      provinceId: resolvedProvinceId,
      valid: true,
      provinceCorrected: true,
    };
  }

  return {
    cityId: resolvedCityId,
    provinceId: resolvedProvinceId,
    valid:
      !Number.isNaN(resolvedCityId) &&
      resolvedCityId > 0 &&
      !Number.isNaN(resolvedProvinceId) &&
      resolvedProvinceId > 0,
  };
}

const CustomerRequestLocationService = {
  getProvincesForCustomerRequest,
  getCitiesForCustomerRequest,
  loadCustomerRequestLocationLists,
  mapProvincesToDropdown,
  mapCitiesToDropdown,
  resolveLocationIdsForSubmit,
};

export default CustomerRequestLocationService;
